"use client";

import React from "react";
import { CircleCheck as CheckCircle2, HardHat, PhoneCall, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    text: "Direct insurance coordination",
    icon: ShieldCheck
  }, {
    text: "Local crews, zero call centers",
    icon: HardHat
  }, {
    text: "Permits, framing & full rebuild",
    icon: CheckCircle2
  }];
  return <section id="hero" data-nav="dark" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-foreground text-background">
      {/* Background Photography with Documentary Framing */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_project_plans_site.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_project_plans_site.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_project_plans_site.png-wvc-sizes" alt="TriServe project manager reviewing structural rebuild plans on site" loading="eager" decoding="async" className="w-full h-full object-cover object-center scale-105" />
        {/* Multi-stop industrial scrim for guaranteed high text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/80 to-foreground/95" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Technical Spec Bar / Operational Status Pill */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-md bg-background/10 border border-background/20 backdrop-blur-xs mb-6 text-xs font-mono uppercase tracking-wider text-background">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            <span>24/7 Active Field Response • MD, DC &amp; Northern VA</span>
          </motion.div>

          {/* Primary H1 Headline */}
          <motion.h1 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.32,
          delay: 0.05
        }} className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-background leading-tight mb-5">
            Reconstruction &amp; Design-Build
          </motion.h1>

          {/* Supporting Headline Line */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.32,
          delay: 0.1
        }} className="text-lg sm:text-xl font-medium text-background/90 leading-relaxed mb-4">
            Turn-key reconstruction managed by the same accountable team that handled your initial disaster mitigation.
          </motion.p>

          {/* Body Paragraph */}
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.32,
          delay: 0.15
        }} className="text-sm sm:text-base text-background/80 leading-relaxed mb-8 max-w-2xl">
            From emergency board-up and structural stabilization to framing, mechanical permits, and complete interior finishes, our local builders restore your property to pre-loss condition without the confusion of handoffs or separate contractors.
          </motion.p>

          {/* Action Button Row */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.32,
          delay: 0.2
        }} className="flex flex-wrap items-center gap-4 mb-10">
            {/* Primary Green Click-to-Call Button */}
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 py-3 rounded-md shadow-md transition-all active:scale-95">
              <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                <PhoneCall className="h-5 w-5" />
                <span>Call Now</span>
              </a>
            </Button>

            {/* Safety-Yellow Request Service Button */}
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-bold px-6 py-3 rounded-md shadow-md transition-all active:scale-95">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                <span>Request Service</span>
              </Link>
            </Button>
          </motion.div>

          {/* Technical Spec Anchors / Trust Strip */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.35,
          delay: 0.25
        }} className="pt-6 border-t border-background/15 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {trustPoints.map((item, index) => {
            const IconComp = item.icon;
            return <div key={index} data-index={index} className="flex items-center gap-2 text-xs font-medium text-background/80">
                  <IconComp className="h-4 w-4 text-accent shrink-0" />
                  <span>{item.text}</span>
                </div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}